import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import { TidewaysContext } from '../app/context'
import Navigator from '../app/navigator'
import { ApplicationState } from '../redux/ApplicationState'
import { parseQueryString } from './queryString'

interface RenderProps {
    service?: string
    environment?: string
    isProductionEnvironment: boolean
    setEnvironment: (environment: string) => void
    setService: (service: string) => void
}

type Props = {
    tidewaysContext: TidewaysContext
    render: (RenderProps) => React.ReactNode
    navigator: Navigator
    routeParameters: object
} & RouteComponentProps<{ s: string; env: string }>

/**
 * Allows you to use and set the application's environment and service.
 *
 * @example <WithEnvironmentAndService render={({ service, setService }) => (
 *              <div>
 *                  <h1>{service}</h1>
 *                  <button onClick={() => setService('worker')}>worker!</button>
 *              </div>
 *          )}/>
 */
class WithEnvironmentAndServiceComponent extends React.Component<Props> {
    public render() {
        const search = parseQueryString(this.props.location.search)

        const service = search.s || this.props.tidewaysContext.application.defaultService || 'web'
        const environment = search.env || this.props.tidewaysContext.application.defaultEnvironment || 'production'
        const isProductionEnvironment =
            environment === 'production' || this.props.tidewaysContext.productionEnvironments.includes(environment)

        return this.props.render({
            isProductionEnvironment,
            service,
            environment,
            setService: this.setService,
            setEnvironment: this.setEnvironment,
        })
    }

    private setService = (service: string) => {
        this.props.tidewaysContext.setService(service)
        this.props.navigator.replace({
            ...this.props.routeParameters,
            s: service,
        })
    }
    private setEnvironment = (environment: string) => {
        this.props.tidewaysContext.setEnvironment(environment)
        this.props.navigator.replace({
            ...this.props.routeParameters,
            env: environment,
        })
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    tidewaysContext: state.tidewaysContext,
    navigator: new Navigator(state.tidewaysContext.route),
    routeParameters: state.tidewaysContext.route.parameters,
})

export const WithEnvironmentAndService = connect(mapStateToProps)(withRouter(WithEnvironmentAndServiceComponent))
